import { IconPartyPopper } from '../ui/icons';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

/**
 * 📧 EMAIL CONFIRMATION PAGE - Page de confirmation d'email
 * ========================================================
 * 
 * ✨ Fonctionnalités :
 * - Confirmation de vérification d'email
 * - Messages d'état (succès/erreur)
 * - Redirection vers connexion
 * - Design harmonisé avec le système auth
 */
const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const token = searchParams.get('token');
  const error = searchParams.get('error');

  // État d'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border border-red-100">
            {/* Icône d'erreur */}
            <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Erreur de confirmation
            </h1>
            <p className="text-gray-600 mb-8">
              Le lien de confirmation est invalide ou a expiré.
            </p>
            
            <div className="space-y-3">
              <Link 
                to="/register"
                className="block w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 text-center"
              >
                Nouvelle inscription
              </Link>
              <Link 
                to="/login"
                className="block w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors duration-200 text-center"
              >
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État de succès
  if (type === 'signup' || token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border border-green-100">
            {/* Icône de succès animée */}
            <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
              <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
              <div className="absolute inset-0 rounded-full bg-green-200 animate-ping opacity-30"></div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Email confirmé !
            </h1>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Votre adresse email a été confirmée avec succès. Vous pouvez maintenant vous connecter à votre compte ImpactInvest.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <IconPartyPopper className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Bienvenue dans la communauté ImpactInvest ! Commencez dès maintenant vos investissements responsables.
              </p>
            </div>
            
            <Link 
              to="/login"
              className="block w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 text-center"
            >
              Se connecter maintenant
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // État par défaut (chargement)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border border-blue-100">
          {/* Icône de chargement */}
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Vérification en cours...
          </h1>
          <p className="text-gray-600 mb-8">
            Nous vérifions votre email, veuillez patienter.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation;